import React from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import CallToAction from '@mui/icons-material/CallToAction';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../../contexts/CalculatorContext';

/**
 * Callback component for the pricing calculator
 * @param {Object} props - Component props
 * @param {number} props.value - Current value (sessions)
 * @param {Function} props.setValue - Function to update value
 * @param {number} props.min - Minimum value
 * @param {number} props.max - Maximum value
 * @param {string} props.description - Product description
 * @param {number} props.price - Calculated price
 * @param {string} props.currency - Currency symbol
 * @param {string} props.id - Product ID
 * @param {Function} props.toggleProduct - Function to toggle product selection
 */
const Callback = ({
  value,
  setValue,
  min,
  max,
  description,
  price,
  currency,
  id,
  toggleProduct
}) => {
  const { t } = useTranslation();
  const { packageTier } = useCalculator();

  // Check if this product is included in the current tier (Pro/Corporate)
  const isIncludedInTier = packageTier === 'pro' || packageTier === 'corporate';

  return (
    <Box sx={{
      borderRadius: 2,
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CallToAction sx={{ color: '#333', fontSize: 20, mr: 1 }} />
          <Typography sx={{ fontWeight: 500 }}>
            {t('calculator.callback', 'Callback')}
          </Typography>
          {isIncludedInTier && (
            <Chip
              label={t('calculator.included', 'INCLUDED')}
              size="small"
              sx={{
                ml: 1,
                bgcolor: 'rgba(0, 200, 83, 0.1)',
                color: '#00C853',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          )}
        </Box>
        <Box>
          {/* Hide close button when included in tier */}
          {!isIncludedInTier && (
            <IconButton
              size="small"
              onClick={() => toggleProduct(id)}
              sx={{ color: '#333', '&:hover': { color: '#00C853' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Slider - hide when included in tier (Pro/Corporate) */}
      {!isIncludedInTier && (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
            {t('calculator.dailyWebsiteSessions', 'Daily website sessions')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Slider
                value={value}
                min={min}
                max={max}
                onChange={(_, v) => setValue(Number(v))}
                sx={{
                  color: '#00C853',
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    backgroundColor: '#00C853',
                  }
                }}
              />
            </Box>
            <TextField
              value={value}
              onChange={e => {
                // Дозволяємо будь-які значення при введенні
                const newValue = e.target.value === '' ? '' : Number(e.target.value);
                setValue(newValue);
              }}
              onBlur={() => {
                // Застосовуємо обмеження при втраті фокусу
                if (value === '' || value < min) {
                  setValue(min);
                } else if (value > max) {
                  setValue(max);
                }
              }}
              type="number"
              variant="outlined"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Box>
        </>
      )}

      {/* Show included message for Pro/Corporate tiers */}
      {isIncludedInTier && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('calculator.includedInTier', 'Included in your {{tier}} plan', { tier: packageTier.charAt(0).toUpperCase() + packageTier.slice(1) })}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(Callback);
